import React, { useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from 'react-redux';
import { addDays, addMonths, compareAsc, startOfMonth, endOfMonth } from "date-fns";
import { useMediaQuery, useTheme, Box, Tooltip, IconButton, Paper, Chip, Typography } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import api from "state/axios";
import { LAYOUT_HEIGHT } from 'state/constants';
import { parseUTCDate } from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import FlexCenter from 'components/FlexCenter';
import DataGridComponent from 'components/DataGridComponent';
import LegMembersDialog from 'components/LegMembersDialog';


const CrewBlockHours = ({ rows, offSet }) => {
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const canView = hasPermission(user?.permissions, "AircraftRoutes", "view", false);

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
    const [selectedCrewCode, setSelectedCrewCode] = useState(null);
    const [selectedCrewName, setSelectedCrewName] = useState(null);
    const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


    const handleViewAircraftRoutes = useCallback(async (param) => {
        if (!startDate || !endDate) return;

        try {
            const crewCode = param.crewCode || '';
            const crewName = param.crewName || '';
            setSelectedCrewCode(crewCode);
            setSelectedCrewName(crewName);

            const startMonth = startOfMonth(startDate);
            const prev = Math.min(offSet, 0);
            const next = Math.max(offSet, 0);

            const fromDate = parseUTCDate(addMonths(startMonth, prev));
            const toDate = parseUTCDate(endOfMonth(addMonths(startMonth, next)));

            const { data } = await api.post(`/aircraftroutes`, {
                startDate: addDays(fromDate, -1),
                endDate: addDays(toDate, 1),
                crewCode,
            });

            const filtered = data.aircraftRoutes?.filter(route => {
                const routeDateToCompare = timeMode === 'UTC'
                    ? route.routeDate
                    : route.routeDateLocalBase;
                return (
                    compareAsc(routeDateToCompare, fromDate) >= 0 &&
                    compareAsc(routeDateToCompare, toDate) <= 0
                );
            });

            const result = filtered?.reduce((acc, cur) => {
                const key = `${cur.routeDate.split('T')[0]}|${cur.routeNo}`;
                const sectorsWithCrew = cur.sectors?.filter(sector =>
                    sector.crewMembers?.some(cm => cm.crewCode === crewCode)
                );
                if (sectorsWithCrew?.length) acc[key] = sectorsWithCrew;
                return acc;
            }, {});

            setGroupedCrewRosters(result);
            setOpenLegMembersDialog(true);
        } catch {
            setOpenLegMembersDialog(false);
        }
    }, [timeMode, startDate, endDate, offSet]);

    const handleCloseLegMembersDialog = () => {
        setGroupedCrewRosters([]);
        setOpenLegMembersDialog(false);
    };


    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            filterable: false,
            sortable: false,
            flex: 1,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        { field: "crewCode", headerName: "Code", flex: 1 },
        { field: "crewName", headerName: "CrewName", flex: 4 },
        { field: "post", headerName: "Post", flex: 1 },
        { field: "base", headerName: "Base", flex: 1 },
        { field: "trainingQuals", headerName: "Trainer", flex: 1 },
        { field: "quals", headerName: "Quals", flex: 3 },
        {
            field: "blockHours",
            headerName: "BlockHours",
            type: "number",
            align: "right",
            flex: 1.5,
            valueFormatter: (value) => value > 0 ? parseFloat(value).toFixed(2) : 0,
        },
    ], []);

    const actionColumn = useMemo(() => {
        if (!canView) return [];
        return [{
            field: "action",
            headerName: "Action",
            filterable: false,
            sortable: false,
            flex: 1.2,
            renderCell: (params) => (
                <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
                    <Tooltip title="View crew routes">
                        <IconButton
                            aria-label="view-crew-routes"
                            sx={{
                                color: theme.palette.primary.dark,
                                '&:hover': { color: theme.palette.green.deep },
                            }}
                            onClick={() => handleViewAircraftRoutes(params.row)}
                        >
                            <Visibility />
                        </IconButton>
                    </Tooltip>
                </FlexCenter>
            ),
        }];
    }, [canView, theme, handleViewAircraftRoutes]);


    const sortedRows = useMemo(() =>
        [...(rows || [])].sort((a, b) => b.blockHours - a.blockHours),
        [rows]
    );


    if (isMobile) {
        return (
            <Box 
                ref={scrollRef} 
                height="100%" 
                sx={{ overflowY: 'auto' }}
            >
                {sortedRows.length === 0 ? (
                    <FlexCenter height={LAYOUT_HEIGHT}>
                        <Typography color="error">No data available</Typography>
                    </FlexCenter>
                ) : (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        width="100%" 
                        gap={2}
                    >
                        {sortedRows.map((row, index) => (
                            <Paper
                                key={row._id}
                                elevation={1}
                                sx={{
                                    p: 1.5,
                                    borderLeft: `2px solid ${theme.palette.primary.main}`,
                                    borderRadius: 1,
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Box flex={1} minWidth={0} mr={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            #{index + 1} · {row.crewCode}
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight="bold" lineHeight={1.3} noWrap>
                                            {row.crewName}
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right" flexShrink={0}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Block Hours
                                        </Typography>
                                        <Typography variant="h6" color="primary" fontWeight="bold" lineHeight={1.2}>
                                            {row.blockHours > 0 ? parseFloat(row.blockHours).toFixed(2) : '0.00'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" gap={0.5} mt={0.75} flexWrap="wrap" alignItems="center">
                                    {row.post && (
                                        <Chip label={row.post} size="small" color="primary" variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {row.base && (
                                        <Chip label={row.base} size="small" variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {row.trainingQuals?.map(q => (
                                        <Chip key={q} label={q} size="small" color="warning" variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }} />
                                    ))}
                                </Box>

                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.75}>
                                    {row.quals ? (
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                                            {row.quals}
                                        </Typography>
                                    ) : <Box flex={1} />}
                                    {canView && (
                                        <Tooltip title="View aircraft routes">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewAircraftRoutes(row)}
                                                sx={{
                                                    color: theme.palette.primary.dark,
                                                    '&:hover': { color: theme.palette.green?.deep },
                                                }}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}

                <ScrollToTop scrollRef={scrollRef} />
                
                {openLegMembersDialog && (
                    <LegMembersDialog
                        open={openLegMembersDialog}
                        onClose={handleCloseLegMembersDialog}
                        crewCode={selectedCrewCode}
                        crewName={selectedCrewName}
                        crewLegs={groupedCrewRosters}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box height="100%">
            <DataGridComponent
                rows={rows || []}
                columns={[...columns, ...actionColumn]}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                props={{
                    disableRowSelectionOnClick: true,
                    initialState: {
                        sorting: { sortModel: [{ field: "blockHours", sort: "desc" }] },
                    },
                }}
            />

            {openLegMembersDialog && (
                <LegMembersDialog
                    open={openLegMembersDialog}
                    onClose={handleCloseLegMembersDialog}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    crewLegs={groupedCrewRosters}
                />
            )}
        </Box>
    );
};

export default CrewBlockHours;
